import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { AlertTriangleIcon, CheckIcon, CloseIcon, InfoIcon } from './icons'
import IconButton from './IconButton'

export type ToastTone = 'success' | 'error' | 'info'

export interface Toast {
  id: number
  tone: ToastTone
  title: string
  text?: string
}

interface ToastApi {
  show: (toast: Omit<Toast, 'id'>) => void
  success: (title: string, text?: string) => void
  error: (title: string, text?: string) => void
  dismiss: (id: number) => void
}

const ToastContext = createContext<ToastApi | null>(null)

const DISMISS_AFTER_MS = 6000

/**
 * Transient confirmations ("Invitation queued", "Configuration saved").
 *
 * The region is `aria-live="polite"` and never `assertive`: these announce completed work, and
 * interrupting a screen-reader user mid-sentence to say "saved" is worse than telling them a
 * moment later. Anything that *blocks* the user gets a dialog, not a toast.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])
  const nextId = useRef(1)

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id))
  }, [])

  const show = useCallback(
    (toast: Omit<Toast, 'id'>) => {
      const id = nextId.current++
      setToasts((current) => [...current, { ...toast, id }])
      setTimeout(() => dismiss(id), DISMISS_AFTER_MS)
    },
    [dismiss],
  )

  const api = useMemo<ToastApi>(
    () => ({
      show,
      dismiss,
      success: (title, text) => show({ tone: 'success', title, text }),
      error: (title, text) => show({ tone: 'error', title, text }),
    }),
    [show, dismiss],
  )

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="toast-region" role="region" aria-live="polite" aria-label="Notifications">
        {toasts.map((toast) => (
          <div key={toast.id} className={`toast toast--${toast.tone}`}>
            <span className="toast__icon">
              {toast.tone === 'success' && <CheckIcon size={18} />}
              {toast.tone === 'error' && <AlertTriangleIcon size={18} />}
              {toast.tone === 'info' && <InfoIcon size={18} />}
            </span>
            <div className="toast__body">
              <p className="toast__title">{toast.title}</p>
              {toast.text && <p className="toast__text">{toast.text}</p>}
            </div>
            <IconButton
              className="toast__close"
              label="Dismiss"
              icon={<CloseIcon size={16} />}
              onClick={() => dismiss(toast.id)}
            />
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  )
}

/**
 * Returns a no-op API when no provider is mounted, so a component rendered in isolation (a test,
 * a story) does not have to be wrapped just to exist.
 */
const NOOP: ToastApi = {
  show: () => {},
  success: () => {},
  error: () => {},
  dismiss: () => {},
}

export function useToast(): ToastApi {
  return useContext(ToastContext) ?? NOOP
}
